// src/pages/ChefeDepartamentoDashboardPage.jsx
import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  Suspense,
  lazy,
} from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Alert,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Tabs,
  Tab,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  AlertTitle,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  LinearProgress,
  AppBar,
  Toolbar,
  IconButton,
  Avatar,
  Menu,
  MenuItem as MenuDropdownItem,
  Breadcrumbs,
  Link,
  SwipeableDrawer,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Skeleton,
  Snackbar,
  Badge,
  Switch,
  FormControlLabel,
  Fade,
  Slide,
  useTheme,
  useMediaQuery,
  alpha,
} from '@mui/material';
import {
  Check as CheckIcon,
  Close as CloseIcon,
  Article as ArticleIcon,
  Assignment as AssignmentIcon,
  Description as DescriptionIcon,
  EventNote as EventNoteIcon,
  Person as PersonIcon,
  School as SchoolIcon,
  Badge as BadgeIcon,
  Visibility as VisibilityIcon,
  ArchiveOutlined as ArchiveIcon,
  Refresh as RefreshIcon,
  Menu as MenuIcon,
  Home as HomeIcon,
  ArrowBack as ArrowBackIcon,
  Logout as LogoutIcon,
  AccountCircle as AccountCircleIcon,
  Notifications as NotificationsIcon,
  DarkMode as DarkModeIcon,
  LightMode as LightModeIcon,
  Dashboard as DashboardIcon,
  TrendingUp as TrendingUpIcon,
  Analytics as AnalyticsIcon,
  NavigateNext as NavigateNextIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';

// Services (mantidos originais)
import {
  listarPropostas,
  aprovarProposta,
  rejeitarProposta,
  obterSimilaridade,
  atualizarSupervisor,
} from '../../services/propostaService';
import {
  listarProjetos,
  encaminharProjetoParaRevisor,
  obterSimilaridadeProjeto,
  aprovarProjeto,
} from '../../services/projetoService';
import {
  listarMonografias,
  obterSimilaridadeMonografia,
  aprovarMonografiaPeloChefe,
  aprovarMonografia,
  devolverMonografia,
  encaminharMonografiaParaRevisao,
} from '../../services/monografiaService';
import { listarSupervisores } from '../../services/supervisorService';
import ChefeDepartamentoService from '../../services/ChefeDepartamentoService';
import { listarRevisores } from '../../services/revisorService';
import PropTypes from 'prop-types';
import useAuth from '../../hooks/useAuth';

// ⚡ Lazy loading (Performance)
const DashboardTCC = lazy(() => import('../DashboardTCC'));
const DefesaList = lazy(() => import('../../components/Defesa/DefesaList'));
const ArquivarMonografia = lazy(() =>
  import('../../components/Monografia/ArquivarMonografia')
);

// ---------- Utils (debounce, cleanup) ----------
function useDebouncedCallback(fn, delay) {
  const t = useRef();
  return (...args) => {
    if (t.current) clearTimeout(t.current);
    t.current = setTimeout(() => fn(...args), delay);
  };
}

// ---------- Componente Header Aprimorado ----------
const DashboardHeader = ({ darkMode, setDarkMode, onBack, userName }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [notificationAnchor, setNotificationAnchor] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { logout } = useAuth();
  const navigate = useNavigate();

  const handleProfileMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);
  const handleNotificationClick = (event) =>
    setNotificationAnchor(event.currentTarget);
  const handleNotificationClose = () => setNotificationAnchor(null);
  const handleLogout = () => {
    logout();
    navigate('/login');
    handleMenuClose();
  };

  return (
    <AppBar
      position="sticky"
      elevation={0}
      sx={{
        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
      }}
    >
      <Toolbar sx={{ justifyContent: 'space-between', gap: 1 }}>
        {/* Left cluster */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton
            color="inherit"
            onClick={onBack}
            sx={{
              bgcolor: alpha(theme.palette.common.white, 0.12),
              '&:hover': { bgcolor: alpha(theme.palette.common.white, 0.22) },
              width: 44,
              height: 44,
            }}
          >
            <ArrowBackIcon />
          </IconButton>

          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant={isMobile ? 'h6' : 'h5'}
              sx={{
                fontWeight: 700,
                color: 'white',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: { xs: 200, sm: 420, md: 700 },
              }}
              title="Dashboard - Chefe do Departamento"
            >
              {isMobile ? 'Dashboard — Chefe Dep.' : 'Dashboard - Chefe do Departamento'}
            </Typography>

            <Breadcrumbs
              separator={
                <NavigateNextIcon fontSize="small" sx={{ color: 'white' }} />
              }
              sx={{
                color: alpha(theme.palette.common.white, 0.9),
                display: { xs: 'none', sm: 'flex' },
              }}
            >
              <Link color="inherit" href="#" underline="hover">
                <HomeIcon sx={{ mr: 0.5, fontSize: 16 }} />
                Início
              </Link>
              <Typography color="inherit" variant="body2">
                Dashboard
              </Typography>
            </Breadcrumbs>
          </Box>
        </Box>

        {/* Right cluster */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FormControlLabel
            sx={{ m: 0 }}
            control={
              <Switch
                checked={darkMode}
                onChange={(e) => setDarkMode(e.target.checked)}
                icon={<LightModeIcon />}
                checkedIcon={<DarkModeIcon />}
                inputProps={{ 'aria-label': 'Alternar tema escuro' }}
              />
            }
            label=""
          />

          <IconButton
            color="inherit"
            onClick={handleNotificationClick}
            sx={{
              bgcolor: alpha(theme.palette.common.white, 0.12),
              '&:hover': { bgcolor: alpha(theme.palette.common.white, 0.22) },
              width: 44,
              height: 44,
            }}
          >
            <Badge badgeContent={3} color="error">
              <NotificationsIcon />
            </Badge>
          </IconButton>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 1 }}>
            <Avatar
              sx={{
                bgcolor: theme.palette.secondary.main,
                width: 36,
                height: 36,
                fontSize: '0.9rem',
              }}
            >
              {userName && typeof userName === 'string'
                ? userName.charAt(0).toUpperCase()
                : 'CD'}
            </Avatar>

            <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
              <Typography
                variant="body2"
                sx={{ color: 'white', fontWeight: 600, lineHeight: 1.1 }}
              >
                {userName || 'Chefe Departamento'}
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: alpha(theme.palette.common.white, 0.85) }}
              >
                Administrador
              </Typography>
            </Box>

            <IconButton
              color="inherit"
              onClick={handleProfileMenuOpen}
              sx={{
                bgcolor: alpha(theme.palette.common.white, 0.12),
                '&:hover': { bgcolor: alpha(theme.palette.common.white, 0.22) },
                width: 44,
                height: 44,
              }}
              aria-label="Abrir menu do perfil"
            >
              <AccountCircleIcon />
            </IconButton>
          </Box>

          {/* Menus */}
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            PaperProps={{ sx: { mt: 1, minWidth: 220, boxShadow: theme.shadows[8] } }}
          >
            <MenuDropdownItem onClick={handleMenuClose}>
              <PersonIcon sx={{ mr: 2 }} /> Meu Perfil
            </MenuDropdownItem>
            <Divider />
            <MenuDropdownItem onClick={handleLogout} sx={{ color: 'error.main' }}>
              <LogoutIcon sx={{ mr: 2 }} /> Sair
            </MenuDropdownItem>
          </Menu>

          <Menu
            anchorEl={notificationAnchor}
            open={Boolean(notificationAnchor)}
            onClose={handleNotificationClose}
            PaperProps={{ sx: { mt: 1, minWidth: 320, maxWidth: 420, boxShadow: theme.shadows[8] } }}
          >
            <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
              <Typography variant="h6">Notificações</Typography>
            </Box>
            <MenuDropdownItem>
              <Box>
                <Typography variant="body2">Nova proposta submetida</Typography>
                <Typography variant="caption" color="text.secondary">
                  Há 2 horas
                </Typography>
              </Box>
            </MenuDropdownItem>
            <MenuDropdownItem>
              <Box>
                <Typography variant="body2">Projeto revisado disponível</Typography>
                <Typography variant="caption" color="text.secondary">
                  Há 1 dia
                </Typography>
              </Box>
            </MenuDropdownItem>
            <MenuDropdownItem>
              <Box>
                <Typography variant="body2">Defesa agendada</Typography>
                <Typography variant="caption" color="text.secondary">
                  Há 2 dias
                </Typography>
              </Box>
            </MenuDropdownItem>
          </Menu>
        </Box>
      </Toolbar>
    </AppBar>
  );
};
DashboardHeader.propTypes = {
  darkMode: PropTypes.bool.isRequired,
  setDarkMode: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  userName: PropTypes.string,
};

// ---------- Sidebar (Swipeable no mobile) ----------
const DashboardSidebar = ({ activeTab, setActiveTab, open, onClose }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const menuItems = [
    { id: 0, label: 'Dashboard', icon: <DashboardIcon />, color: theme.palette.primary.main },
    { id: 1, label: 'Propostas', icon: <ArticleIcon />, color: theme.palette.info.main },
    { id: 2, label: 'Projetos', icon: <AssignmentIcon />, color: theme.palette.success.main },
    { id: 3, label: 'Monografias', icon: <DescriptionIcon />, color: theme.palette.warning.main },
    { id: 4, label: 'Defesas', icon: <EventNoteIcon />, color: theme.palette.error.main },
    { id: 5, label: 'Avaliações', icon: <VisibilityIcon />, color: theme.palette.secondary.main },
    { id: 6, label: 'Arquivamento', icon: <ArchiveIcon />, color: theme.palette.grey[600] },
    { id: 7, label: 'Dashboard TCC', icon: <AnalyticsIcon />, color: theme.palette.primary.dark },
  ];

  const listContent = (
    <Box
      sx={{
        width: 280,
        boxSizing: 'border-box',
        background: `linear-gradient(180deg, ${theme.palette.background.paper} 0%, ${alpha(
          theme.palette.primary.main,
          0.02
        )} 100%)`,
        borderRight: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        height: '100%',
      }}
      role="navigation"
    >
      <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
          Menu Principal
        </Typography>
      </Box>

      <List sx={{ px: 2, py: 1 }}>
        {menuItems.map((item) => (
          <ListItem
            key={item.id}
            button
            onClick={() => {
              setActiveTab(item.id);
              onClose();
            }}
            sx={{
              mb: 1,
              borderRadius: 2,
              backgroundColor: activeTab === item.id ? alpha(item.color, 0.1) : 'transparent',
              border:
                activeTab === item.id
                  ? `1px solid ${alpha(item.color, 0.3)}`
                  : '1px solid transparent',
              transition: 'all .2s ease-in-out',
              '&:hover': {
                backgroundColor: alpha(item.color, 0.06),
                transform: 'translateX(4px)',
              },
            }}
          >
            <ListItemIcon
              sx={{ color: activeTab === item.id ? item.color : 'text.secondary', minWidth: 40 }}
            >
              {item.icon}
            </ListItemIcon>
            <ListItemText
              primary={item.label}
              sx={{
                '& .MuiListItemText-primary': {
                  fontWeight: activeTab === item.id ? 700 : 500,
                  color: activeTab === item.id ? item.color : 'text.primary',
                },
              }}
            />
          </ListItem>
        ))}
      </List>
    </Box>
  );

  // Swipeable no mobile; Drawer temporário nos restantes casos
  return isMobile ? (
    <SwipeableDrawer
      anchor="left"
      open={open}
      onOpen={() => {}}
      onClose={onClose}
      disableBackdropTransition={false}
      ModalProps={{ keepMounted: true }}
      PaperProps={{ sx: { touchAction: 'pan-y' } }}
    >
      {listContent}
    </SwipeableDrawer>
  ) : (
    <Drawer variant="temporary" anchor="left" open={open} onClose={onClose}>
      {listContent}
    </Drawer>
  );
};
DashboardSidebar.propTypes = {
  activeTab: PropTypes.number.isRequired,
  setActiveTab: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

// ---------- Skeleton ----------
const LoadingSkeleton = () => (
  <Box sx={{ p: 3 }}>
    <Skeleton variant="rectangular" width="100%" height={60} sx={{ mb: 3, borderRadius: 2 }} />
    <Grid container spacing={3}>
      {[1, 2, 3].map((item) => (
        <Grid item xs={12} md={4} key={item}>
          <Skeleton variant="rectangular" width="100%" height={120} sx={{ borderRadius: 2 }} />
        </Grid>
      ))}
    </Grid>
    <Skeleton variant="rectangular" width="100%" height={300} sx={{ mt: 3, borderRadius: 2 }} />
  </Box>
);

// ---------- Toast ----------
const ToastNotification = ({ open, message, severity, onClose }) => (
  <Snackbar
    open={open}
    autoHideDuration={4000}
    onClose={onClose}
    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
  >
    <Alert onClose={onClose} severity={severity} sx={{ width: '100%' }}>
      {message}
    </Alert>
  </Snackbar>
);
ToastNotification.propTypes = {
  open: PropTypes.bool.isRequired,
  message: PropTypes.string.isRequired,
  severity: PropTypes.oneOf(['error', 'warning', 'info', 'success']).isRequired,
  onClose: PropTypes.func.isRequired,
};

// ---------- Seção Expansível (colapsa no mobile) ----------
const ExpandableSection = ({ initiallyOpen = false, children, title }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [open, setOpen] = useState(!isMobile ? true : initiallyOpen);

  useEffect(() => {
    // se alternar para mobile/desktop em runtime
    setOpen(!isMobile ? true : initiallyOpen);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile]);

  return (
    <Box sx={{ borderTop: `1px solid ${alpha(theme.palette.divider, 0.4)}`, mt: 2, pt: 1 }}>
      <Button
        onClick={() => setOpen((v) => !v)}
        startIcon={
          <ExpandMoreIcon
            sx={{
              transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform .2s',
            }}
          />
        }
        sx={{
          borderRadius: 2,
          px: 1.5,
          py: 1,
          minHeight: 40,
          textTransform: 'none',
        }}
      >
        {open ? `Ocultar ${title || 'detalhes'}` : `Ver ${title || 'detalhes'}`}
      </Button>

      <Fade in={open} unmountOnExit>
        <Box sx={{ mt: 2 }}>{children}</Box>
      </Fade>
    </Box>
  );
};
ExpandableSection.propTypes = {
  initiallyOpen: PropTypes.bool,
  children: PropTypes.node,
  title: PropTypes.string,
};

// =====================================================
// ===================== PÁGINA ========================
// =====================================================
function ChefeDepartamentoDashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const fullScreenDialog = useMediaQuery(theme.breakpoints.down('sm'));

  const [activeTab, setActiveTab] = useState(0);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(false);

  // Toast notifications
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });
  const showToast = (message, severity = 'success') =>
    setToast({ open: true, message, severity });

  // Estados gerais
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [parecerModalOpen, setParecerModalOpen] = useState(false);
  const [selectedParecer, setSelectedParecer] = useState('');

  // Propostas
  const [propostas, setPropostas] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedProposta, setSelectedProposta] = useState(null);
  const [motivoRejeicao, setMotivoRejeicao] = useState('');
  const [similaridades, setSimilaridades] = useState({});
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [observacoesProposta, setObservacoesProposta] = useState('');
  const [supervisorDialogOpen, setSupervisorDialogOpen] = useState(false);
  const [supervisores, setSupervisores] = useState([]);
  const [selectedSupervisor, setSelectedSupervisor] = useState('');
  const [filtroStatusProposta, setFiltroStatusProposta] = useState('PENDENTE');
  const [pagePropostas, setPagePropostas] = useState(1);
  const pageSizePropostas = 5;

  // Projetos
  const [projetos, setProjetos] = useState([]);
  const [projetoRevisorDialogOpen, setProjetoRevisorDialogOpen] = useState(false);
  const [selectedProjeto, setSelectedProjeto] = useState(null);
  const [revisores, setRevisores] = useState([]);
  const [selectedRevisor, setSelectedRevisor] = useState('');
  const [similaridadesProjetos, setSimilaridadesProjetos] = useState({});
  const [filtroStatusProjeto, setFiltroStatusProjeto] = useState('PENDENTE');
  const [pageProjetos, setPageProjetos] = useState(1);
  const pageSizeProjetos = 5;

  // Monografias
  const [monografias, setMonografias] = useState([]);
  const [monografiaRevisorDialogOpen, setMonografiaRevisorDialogOpen] = useState(false);
  const [selectedMonografia, setSelectedMonografia] = useState(null);
  const [selectedRevisorMonografia, setSelectedRevisorMonografia] = useState('');
  const [similaridadesMonografias, setSimilaridadesMonografias] = useState({});
  const [filtroStatusMonografia, setFiltroStatusMonografia] = useState('PENDENTE');
  const [pageMonografias, setPageMonografias] = useState(1);
  const pageSizeMonografias = 5;

  // Controle de ciclo de vida/cleanup
  const isMounted = useRef(true);
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  // ---------- Utilidades ----------
  const handleVerParecerMonografia = (monografia) => {
    if (!monografia || !monografia.parecer_revisor) {
      setSelectedParecer('Esta monografia ainda não possui parecer do revisor.');
    } else {
      let parecer = monografia.parecer_revisor;
      if (monografia.data_revisao) {
        parecer += `\n\nData da revisão: ${new Date(monografia.data_revisao).toLocaleDateString()}`;
      }
      if (monografia.revisor_id) {
        const nomeRevisor =
          monografia.revisor?.nome ||
          monografia.projeto?.revisor?.nome ||
          `Revisor ID: ${monografia.revisor_id}`;
        parecer += `\n\nRevisor: ${nomeRevisor}`;
      }
      setSelectedParecer(parecer);
    }
    setParecerModalOpen(true);
  };

  const monografiasHistorico = useMemo(
    () => monografias.filter((m) => m.data_revisao),
    [monografias]
  );

  // ---------- Similaridades ----------
  const carregarSimilaridades = async (propostasCarregadas) => {
    try {
      const novasSimilaridades = {};
      for (const proposta of propostasCarregadas) {
        if (proposta.status === 'PENDENTE') {
          try {
            const similaridade = await obterSimilaridade(proposta.id);
            if (similaridade.length > 0) {
              const dadosFormatados = similaridade.map((s) => ({
                propostaId: s.propostaId,
                similaridadeDireta: parseFloat(s.similaridadeDireta || 0),
                jaroWinkler: parseFloat(s.jaroWinkler || 0),
                similaridade: parseFloat(s.similaridade || 0),
                termosComuns: Array.isArray(s.termosComuns) ? s.termosComuns : [],
                propostaOriginal: {
                  titulo: s.propostaOriginal?.titulo || 'Sem título',
                  problemaPesquisa: s.propostaOriginal?.problemaPesquisa || 'Sem problema',
                  estudante: s.propostaOriginal?.estudante || 'Sem estudante',
                  dataSubmissao: s.propostaOriginal?.dataSubmissao || null,
                },
              }));
              novasSimilaridades[proposta.id] = dadosFormatados;
            }
          } catch (err) {
            console.error(`Erro ao carregar similaridade da proposta ${proposta.id}:`, err);
          }
        }
      }
      if (isMounted.current) setSimilaridades(novasSimilaridades);
    } catch (error) {
      console.error('Erro ao carregar similaridades (Propostas):', error);
    }
  };

  const carregarSimilaridadesProjetos = async (projetosCarregados) => {
    try {
      const novas = {};
      for (const projeto of projetosCarregados) {
        if (projeto.status === 'PENDENTE' && projeto.documento_projeto) {
          try {
            const similaridade = await obterSimilaridadeProjeto(projeto.id);
            if (similaridade.length > 0) {
              novas[projeto.id] = similaridade;
            }
          } catch (err) {
            console.error(`Erro ao carregar similaridade do projeto ${projeto.id}:`, err);
          }
        }
      }
      if (isMounted.current) setSimilaridadesProjetos(novas);
    } catch (error) {
      console.error('Erro ao carregar similaridades (Projetos):', error);
    }
  };

  const carregarSimilaridadesMonografias = async (monografiasCarregadas) => {
    try {
      const novas = {};
      for (const monografia of monografiasCarregadas) {
        if (['PENDENTE', 'SUBMETIDA'].includes(monografia.status)) {
          try {
            const similaridade = await obterSimilaridadeMonografia(monografia.id);
            if (similaridade.length > 0) novas[monografia.id] = similaridade;
          } catch (err) {
            console.error(`Erro ao carregar similaridade da monografia ${monografia.id}:`, err);
          }
        }
      }
      if (isMounted.current) setSimilaridadesMonografias(novas);
    } catch (error) {
      console.error('Erro ao carregar similaridades (Monografias):', error);
    }
  };

  // ---------- Listas auxiliares ----------
  const carregarSupervisores = async () => {
    try {
      const response = await listarSupervisores();
      if (isMounted.current) setSupervisores(response.data);
    } catch (error) {
      console.error('Erro ao carregar supervisores:', error);
      setError('Erro ao carregar lista de supervisores');
    }
  };
  const carregarRevisores = async () => {
    try {
      const response = await listarRevisores();
      if (isMounted.current) setRevisores(response.data);
    } catch (error) {
      console.error('Erro ao carregar revisores:', error);
      setError('Erro ao carregar lista de revisores');
    }
  };

  // ---------- Carregamento de dados ----------
  const fetchDados = async () => {
    try {
      setLoading(true);

      if (activeTab === 0) {
        const [p, pr, m] = await Promise.all([
          listarPropostas(),
          listarProjetos(),
          listarMonografias(),
        ]);
        if (!isMounted.current) return;
        setPropostas(p.data);
        setProjetos(pr.data);
        setMonografias(m.data);
      } else if (activeTab === 1) {
        const response = await listarPropostas();
        if (!isMounted.current) return;
        setPropostas(response.data);
        await carregarSimilaridades(response.data);
        await carregarSupervisores();
      } else if (activeTab === 2) {
        const response = await listarProjetos();
        if (!isMounted.current) return;
        setProjetos(response.data);
        await carregarSimilaridadesProjetos(response.data);
        await carregarRevisores();
      } else if (activeTab === 3) {
        const response = await listarMonografias();
        if (!isMounted.current) return;
        setMonografias(response.data);
        await carregarSimilaridadesMonografias(response.data);
        await carregarRevisores();
      } else if (activeTab === 4) {
        // Defesas — componente lazy trata da busca interna se necessário
      } else if (activeTab === 5) {
        // Avaliações — componente lazy trata da busca interna
      } else if (activeTab === 6) {
        const responseMonografias = await listarMonografias();
        try {
          const { listarArquivosDigitais } = require('../../services/arquivoDigitalService');
          const responseArquivos = await listarArquivosDigitais();
          const monografiasArquivadas = {};
          responseArquivos.forEach((arquivo) => {
            if (arquivo.monografia_id) monografiasArquivadas[arquivo.monografia_id] = true;
          });
          const processadas = responseMonografias.data.map((monografia) => ({
            ...monografia,
            arquivada: !!monografiasArquivadas[monografia.id],
          }));
          if (isMounted.current) setMonografias(processadas);
        } catch (e) {
          console.error('Erro ao buscar arquivos digitais:', e);
          if (isMounted.current) setMonografias(responseMonografias.data);
        }
      }

      setError(null);
    } catch (err) {
      console.error('Erro ao carregar dados:', err);
      setError('Não foi possível carregar os dados. Por favor, tente novamente.');
      showToast('Erro ao carregar dados', 'error');
    } finally {
      if (isMounted.current) setLoading(false);
    }
  };

  const debouncedFetch = useDebouncedCallback(fetchDados, 80);
  useEffect(() => {
    debouncedFetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // ---------- Actions Propostas ----------
  const openApproveDialog = (proposta) => {
    setSelectedProposta(proposta);
    setApproveDialogOpen(true);
  };
  const handleAprovarPropostaConfirm = async () => {
    try {
      await aprovarProposta(selectedProposta.id, observacoesProposta);
      setSuccessMessage('Proposta aprovada com sucesso!');
      showToast('Proposta aprovada com sucesso!', 'success');
      setApproveDialogOpen(false);
      setObservacoesProposta('');
      setSelectedProposta(null);
      await fetchDados();
    } catch (err) {
      console.error('Erro ao aprovar proposta:', err);
      setError('Erro ao aprovar proposta. Por favor, tente novamente.');
      showToast('Erro ao aprovar proposta', 'error');
    }
  };
  const openRejectDialog = (proposta) => {
    setSelectedProposta(proposta);
    setDialogOpen(true);
  };
  const handleRejeitarProposta = async () => {
    try {
      await rejeitarProposta(selectedProposta.id, motivoRejeicao);
      setDialogOpen(false);
      setMotivoRejeicao('');
      setSelectedProposta(null);
      showToast('Proposta rejeitada com sucesso!', 'success');
      await fetchDados();
    } catch (err) {
      console.error('Erro ao rejeitar proposta:', err);
      setError('Erro ao rejeitar proposta. Por favor, tente novamente.');
      showToast('Erro ao rejeitar proposta', 'error');
    }
  };

  // ---------- Actions Projeto ----------
  const handleEmitirCredencial = async (projeto) => {
    try {
      await aprovarProjeto(projeto.id, {
        observacoes: '',
        emitirCredencial: true,
        numeroCredencial: '',
      });
      await fetchDados();
      showToast('Credencial emitida com sucesso!', 'success');
    } catch (err) {
      console.error(err);
      showToast(
        'Erro ao emitir credencial: ' + (err.response?.data?.message || err.message),
        'error'
      );
    }
  };

  const openSupervisorDialog = (proposta) => {
    setSelectedProposta(proposta);
    setSelectedSupervisor(proposta.supervisorId || '');
    setSupervisorDialogOpen(true);
  };
  const handleDesignarSupervisor = async () => {
    try {
      await atualizarSupervisor(selectedProposta.id, selectedSupervisor);
      setSupervisorDialogOpen(false);
      setSelectedSupervisor('');
      setSelectedProposta(null);
      showToast('Supervisor designado com sucesso!', 'success');
      await fetchDados();
    } catch (err) {
      console.error('Erro ao designar supervisor:', err);
      setError('Erro ao designar supervisor. Por favor, tente novamente.');
      showToast('Erro ao designar supervisor', 'error');
    }
  };

  const openProjetoRevisorDialog = (projeto) => {
    setSelectedProjeto(projeto);
    setSelectedRevisor('');
    setProjetoRevisorDialogOpen(true);
  };
  const handleEncaminharProjetoParaRevisor = async () => {
    try {
      await encaminharProjetoParaRevisor(selectedProjeto.id, selectedRevisor);
      setProjetoRevisorDialogOpen(false);
      setSelectedRevisor('');
      setSelectedProjeto(null);
      showToast('Projeto encaminhado para revisor com sucesso!', 'success');
      await fetchDados();
    } catch (err) {
      console.error('Erro ao encaminhar projeto para revisor:', err);
      setError('Erro ao encaminhar projeto para revisor. Por favor, tente novamente.');
      showToast('Erro ao encaminhar projeto para revisor', 'error');
    }
  };

  // ---------- Actions Monografia ----------
  const openMonografiaRevisorDialog = (monografia) => {
    setSelectedMonografia(monografia);
    setSelectedRevisorMonografia('');
    setMonografiaRevisorDialogOpen(true);
  };
  const handleEncaminharMonografiaParaRevisao = async () => {
    try {
      await encaminharMonografiaParaRevisao(selectedMonografia.id, selectedRevisorMonografia);
      setMonografiaRevisorDialogOpen(false);
      setSelectedRevisorMonografia('');
      setSelectedMonografia(null);
      showToast('Monografia encaminhada para revisão com sucesso!', 'success');
      await fetchDados();
    } catch (err) {
      console.error('Erro ao encaminhar monografia para revisão:', err);
      setError('Erro ao encaminhar monografia para revisão. Por favor, tente novamente.');
      showToast('Erro ao encaminhar monografia para revisão', 'error');
    }
  };

  const handleAprovarMonografia = async (monografiaId) => {
    try {
      setLoading(true);
      await aprovarMonografiaPeloChefe(monografiaId);
      showToast('Monografia aprovada com sucesso!', 'success');
      await fetchDados();
    } catch (error) {
      console.error(error);
      setError('Erro ao aprovar monografia');
      showToast('Erro ao aprovar monografia', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDevolverMonografia = async (monografiaId) => {
    try {
      setLoading(true);
      const comentario = window.prompt('Digite o motivo da devolução:');
      await devolverMonografia(monografiaId, comentario || '');
      showToast('Monografia devolvida com sucesso!', 'success');
      await fetchDados();
    } catch (error) {
      console.error(error);
      setError('Erro ao devolver monografia');
      showToast('Erro ao devolver monografia', 'error');
    } finally {
      setLoading(false);
    }
  };

  // ---------- Paginação e filtros ----------
  const propostasFiltradas = useMemo(() => {
    if (!filtroStatusProposta) return propostas;
    return propostas.filter((p) => p.status === filtroStatusProposta);
  }, [propostas, filtroStatusProposta]);
  const totalPropostas = propostasFiltradas.length;
  const totalPagesPropostas = Math.ceil(totalPropostas / pageSizePropostas);
  const startIndexPropostas = (pagePropostas - 1) * pageSizePropostas;
  const propostasPagina = propostasFiltradas.slice(
    startIndexPropostas,
    startIndexPropostas + pageSizePropostas
  );
  useEffect(() => {
    if (pagePropostas > totalPagesPropostas && totalPagesPropostas > 0) {
      setPagePropostas(totalPagesPropostas);
    }
  }, [pagePropostas, totalPagesPropostas]);

  const projetosFiltrados = useMemo(() => {
    if (!filtroStatusProjeto) return projetos;
    return projetos.filter((p) => p.status === filtroStatusProjeto);
  }, [projetos, filtroStatusProjeto]);
  const totalProjetos = projetosFiltrados.length;
  const totalPagesProjetos = Math.ceil(totalProjetos / pageSizeProjetos);
  const startIndexProjetos = (pageProjetos - 1) * pageSizeProjetos;
  const projetosPagina = projetosFiltrados.slice(
    startIndexProjetos,
    startIndexProjetos + pageSizeProjetos
  );
  useEffect(() => {
    if (pageProjetos > totalPagesProjetos && totalPagesProjetos > 0) {
      setPageProjetos(totalPagesProjetos);
    }
  }, [pageProjetos, totalPagesProjetos]);

  const monografiasFiltradas = useMemo(() => {
    if (!filtroStatusMonografia) return monografias;
    if (filtroStatusMonografia === 'SUBMETIDA') {
      return monografias.filter(
        (m) =>
          m.status === 'SUBMETIDA' || m.status === 'SUBMETIDA_A_DIRECCAO_CIENTIFICA'
      );
    }
    return monografias.filter((m) => m.status === filtroStatusMonografia);
  }, [monografias, filtroStatusMonografia]);
  const totalMonografias = monografiasFiltradas.length;
  const totalPagesMonografias = Math.ceil(totalMonografias / pageSizeMonografias);
  const startIndexMonografias = (pageMonografias - 1) * pageSizeMonografias;
  const monografiasPagina = monografiasFiltradas.slice(
    startIndexMonografias,
    startIndexMonografias + pageSizeMonografias
  );
  useEffect(() => {
    if (pageMonografias > totalPagesMonografias && totalPagesMonografias > 0) {
      setPageMonografias(totalPagesMonografias);
    }
  }, [pageMonografias, totalPagesMonografias]);

  // ---------- DASHBOARD ----------
  const renderDashboardStatistics = () => {
    const statistics = {
      propostas: {
        pendentes: propostas.filter((p) => p.status === 'PENDENTE').length,
        aprovadas: propostas.filter((p) => p.status === 'APROVADA').length,
        rejeitadas: propostas.filter((p) => p.status === 'REJEITADA').length,
      },
      projetos: {
        pendentes: projetos.filter((p) => p.status === 'PENDENTE').length,
        emRevisao: projetos.filter((p) => p.status === 'EM_REVISAO').length,
        revisados: projetos.filter((p) => p.status === 'REVISADO').length,
        aprovados: projetos.filter((p) => p.status === 'APROVADO').length,
      },
      monografias: {
        pendentes: monografias.filter((m) => m.status === 'PENDENTE').length,
        submetidas: monografias.filter(
          (m) =>
            m.status === 'SUBMETIDA' ||
            m.status === 'SUBMETIDA_A_DIRECCAO_CIENTIFICA'
        ).length,
        emRevisao: monografias.filter((m) => m.status === 'EM_REVISAO').length,
        aprovadas: monografias.filter((m) => m.status === 'APROVADA').length,
      },
    };

    const pieData = [
      { name: 'Propostas', value: propostas.length, color: theme.palette.primary.main },
      { name: 'Projetos', value: projetos.length, color: theme.palette.success.main },
      { name: 'Monografias', value: monografias.length, color: theme.palette.warning.main },
    ];

    const barData = [
      {
        name: 'Pendentes',
        propostas: statistics.propostas.pendentes,
        projetos: statistics.projetos.pendentes,
        monografias: statistics.monografias.pendentes,
      },
      {
        name: 'Em Andamento',
        propostas: 0,
        projetos: statistics.projetos.emRevisao,
        monografias: statistics.monografias.emRevisao,
      },
      {
        name: 'Concluídos',
        propostas: statistics.propostas.aprovadas,
        projetos: statistics.projetos.aprovados,
        monografias: statistics.monografias.aprovadas,
      },
    ];

    return (
      <Fade in timeout={800}>
        <Box>
          {/* Header Estatísticas */}
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2.5, md: 4 },
              mb: 4,
              background: `linear-gradient(135deg, ${alpha(
                theme.palette.primary.main,
                0.1
              )} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
              borderRadius: 3,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <TrendingUpIcon sx={{ fontSize: 36, color: theme.palette.primary.main, mr: 2 }} />
              <Box>
                <Typography
                  variant={isMobile ? 'h5' : 'h4'}
                  sx={{ fontWeight: 800, color: theme.palette.primary.main }}
                >
                  Visão Geral do Sistema
                </Typography>
                <Typography variant="subtitle1" color="text.secondary">
                  Estatísticas e métricas em tempo real
                </Typography>
              </Box>
            </Box>

            {/* Cards principais */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} md={4}>
                <Card
                  elevation={0}
                  sx={{
                    p: 3,
                    height: '100%',
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                    color: 'white',
                    transition: 'transform .3s',
                    '&:hover': { transform: 'translateY(-6px)' },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <ArticleIcon sx={{ fontSize: 36 }} />
                    <Typography variant="h3" sx={{ fontWeight: 800 }}>
                      {propostas.length}
                    </Typography>
                  </Box>
                  <Typography variant="h6" gutterBottom>
                    Propostas
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1.5, mt: 1 }}>
                    <Chip
                      label={`${statistics.propostas.pendentes} Pendentes`}
                      size="small"
                      sx={{ bgcolor: alpha('#fff', 0.2), color: '#fff' }}
                    />
                    <Chip
                      label={`${statistics.propostas.aprovadas} Aprovadas`}
                      size="small"
                      sx={{ bgcolor: alpha('#fff', 0.2), color: '#fff' }}
                    />
                  </Box>
                </Card>
              </Grid>

              <Grid item xs={12} md={4}>
                <Card
                  elevation={0}
                  sx={{
                    p: 3,
                    height: '100%',
                    background: `linear-gradient(135deg, ${theme.palette.success.main} 0%, ${theme.palette.success.dark} 100%)`,
                    color: 'white',
                    transition: 'transform .3s',
                    '&:hover': { transform: 'translateY(-6px)' },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <AssignmentIcon sx={{ fontSize: 36 }} />
                    <Typography variant="h3" sx={{ fontWeight: 800 }}>
                      {projetos.length}
                    </Typography>
                  </Box>
                  <Typography variant="h6" gutterBottom>
                    Projetos
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1.5, mt: 1 }}>
                    <Chip
                      label={`${statistics.projetos.pendentes} Pendentes`}
                      size="small"
                      sx={{ bgcolor: alpha('#fff', 0.2), color: '#fff' }}
                    />
                    <Chip
                      label={`${statistics.projetos.aprovados} Aprovados`}
                      size="small"
                      sx={{ bgcolor: alpha('#fff', 0.2), color: '#fff' }}
                    />
                  </Box>
                </Card>
              </Grid>

              <Grid item xs={12} md={4}>
                <Card
                  elevation={0}
                  sx={{
                    p: 3,
                    height: '100%',
                    background: `linear-gradient(135deg, ${theme.palette.warning.main} 0%, ${theme.palette.warning.dark} 100%)`,
                    color: 'white',
                    transition: 'transform .3s',
                    '&:hover': { transform: 'translateY(-6px)' },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <DescriptionIcon sx={{ fontSize: 36 }} />
                    <Typography variant="h3" sx={{ fontWeight: 800 }}>
                      {monografias.length}
                    </Typography>
                  </Box>
                  <Typography variant="h6" gutterBottom>
                    Monografias
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1.5, mt: 1 }}>
                    <Chip
                      label={`${statistics.monografias.pendentes} Pendentes`}
                      size="small"
                      sx={{ bgcolor: alpha('#fff', 0.2), color: '#fff' }}
                    />
                    <Chip
                      label={`${statistics.monografias.aprovadas} Aprovadas`}
                      size="small"
                      sx={{ bgcolor: alpha('#fff', 0.2), color: '#fff' }}
                    />
                  </Box>
                </Card>
              </Grid>
            </Grid>

            {/* Gráficos */}
            <Grid container spacing={4}>
              <Grid item xs={12} md={6}>
                <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                    <AnalyticsIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
                    Distribuição por Categoria
                  </Typography>
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie
                        data={pieData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        outerRadius={80}
                        dataKey="value"
                      >
                        {pieData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </Paper>
              </Grid>

              <Grid item xs={12} md={6}>
                <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                    <TrendingUpIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
                    Status por Categoria
                  </Typography>
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={barData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="propostas" stackId="a" fill={theme.palette.primary.main} name="Propostas" />
                      <Bar dataKey="projetos" stackId="a" fill={theme.palette.success.main} name="Projetos" />
                      <Bar dataKey="monografias" stackId="a" fill={theme.palette.warning.main} name="Monografias" />
                    </BarChart>
                  </ResponsiveContainer>
                </Paper>
              </Grid>
            </Grid>
          </Paper>

          {/* Ações Rápidas (lista vertical no mobile; grelha no desktop) */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
              gap: 3,
              scrollSnapType: { xs: 'x mandatory', md: 'none' },
              overflowX: { xs: 'auto', md: 'visible' },
              pb: { xs: 2, md: 0 },
              WebkitOverflowScrolling: 'touch',
            }}
          >
            <Card
              elevation={2}
              sx={{
                p: 3,
                height: '100%',
                cursor: 'pointer',
                transition: 'all .3s',
                scrollSnapAlign: 'start',
                '&:hover': { transform: { md: 'translateY(-4px)' } },
              }}
              onClick={() => setActiveTab(1)}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <ArticleIcon sx={{ fontSize: 36, color: theme.palette.primary.main, mr: 2 }} />
                <Box>
                  <Typography variant="h6" color="primary">
                    Gestão de Propostas
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {statistics.propostas.pendentes} pendentes de aprovação
                  </Typography>
                </Box>
              </Box>
              <LinearProgress
                variant="determinate"
                value={(statistics.propostas.aprovadas / Math.max(propostas.length, 1)) * 100}
                sx={{ mb: 2, height: 8, borderRadius: 4 }}
              />
              <Button variant="contained" fullWidth sx={{ minHeight: 44 }}>
                Administrar Propostas
              </Button>
            </Card>

            <Card
              elevation={2}
              sx={{
                p: 3,
                height: '100%',
                cursor: 'pointer',
                transition: 'all .3s',
                scrollSnapAlign: 'start',
                '&:hover': { transform: { md: 'translateY(-4px)' } },
              }}
              onClick={() => setActiveTab(2)}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <AssignmentIcon sx={{ fontSize: 36, color: theme.palette.success.main, mr: 2 }} />
                <Box>
                  <Typography variant="h6" sx={{ color: 'success.main' }}>
                    Gestão de Projectos
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {statistics.projetos.pendentes} aguardando revisão
                  </Typography>
                </Box>
              </Box>
              <LinearProgress
                variant="determinate"
                value={(statistics.projetos.aprovados / Math.max(projetos.length, 1)) * 100}
                sx={{ mb: 2, height: 8, borderRadius: 4 }}
                color="success"
              />
              <Button variant="contained" color="success" fullWidth sx={{ minHeight: 44 }}>
                Administrar Projectos
              </Button>
            </Card>

            <Card
              elevation={2}
              sx={{
                p: 3,
                height: '100%',
                cursor: 'pointer',
                transition: 'all .3s',
                scrollSnapAlign: 'start',
                '&:hover': { transform: { md: 'translateY(-4px)' } },
              }}
              onClick={() => setActiveTab(3)}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <DescriptionIcon sx={{ fontSize: 36, color: theme.palette.warning.main, mr: 2 }} />
                <Box>
                  <Typography variant="h6" sx={{ color: 'warning.main' }}>
                    Gestão de Monografias
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {statistics.monografias.pendentes} aguardando análise
                  </Typography>
                </Box>
              </Box>
              <LinearProgress
                variant="determinate"
                value={(statistics.monografias.aprovadas / Math.max(monografias.length, 1)) * 100}
                sx={{ mb: 2, height: 8, borderRadius: 4 }}
                color="warning"
              />
              <Button variant="contained" color="warning" fullWidth sx={{ minHeight: 44 }}>
                Administrar Monografias
              </Button>
            </Card>
          </Box>
        </Box>
      </Fade>
    );
  };

  // ---------- PROPOSTAS ----------
  const renderPropostas = () => (
    <Fade in timeout={600}>
      <Card elevation={2} sx={{ borderRadius: 3 }}>
        <CardContent sx={{ p: { xs: 2, md: 4 } }}>
          <Box sx={{ mb: 4 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: { xs: 'stretch', sm: 'center' },
                gap: 1,
                mb: 3,
                pb: 2,
                borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                flexWrap: 'wrap',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <ArticleIcon sx={{ fontSize: 28, color: theme.palette.primary.main, mr: 1.5 }} />
                <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
                  Gestão de Propostas
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button
                  variant={filtroStatusProposta === 'PENDENTE' ? 'contained' : 'outlined'}
                  onClick={() => {
                    setFiltroStatusProposta('PENDENTE');
                    setPagePropostas(1);
                  }}
                  size="small"
                  sx={{ borderRadius: 2, minHeight: 40 }}
                >
                  Pendentes ({propostas.filter((p) => p.status === 'PENDENTE').length})
                </Button>
                <Button
                  variant={filtroStatusProposta === 'APROVADA' ? 'contained' : 'outlined'}
                  onClick={() => {
                    setFiltroStatusProposta('APROVADA');
                    setPagePropostas(1);
                  }}
                  size="small"
                  color="success"
                  sx={{ borderRadius: 2, minHeight: 40 }}
                >
                  Aprovadas ({propostas.filter((p) => p.status === 'APROVADA').length})
                </Button>
                <Button
                  variant={filtroStatusProposta === 'REJEITADA' ? 'contained' : 'outlined'}
                  onClick={() => {
                    setFiltroStatusProposta('REJEITADA');
                    setPagePropostas(1);
                  }}
                  size="small"
                  color="error"
                  sx={{ borderRadius: 2, minHeight: 40 }}
                >
                  Rejeitadas ({propostas.filter((p) => p.status === 'REJEITADA').length})
                </Button>
              </Box>
            </Box>

            {propostasFiltradas.length === 0 ? (
              <Alert
                severity="info"
                sx={{
                  borderRadius: 2,
                  border: `1px solid ${alpha(theme.palette.info.main, 0.28)}`,
                  bgcolor: alpha(theme.palette.info.main, 0.06),
                }}
              >
                Não há propostas{' '}
                {filtroStatusProposta === 'PENDENTE'
                  ? 'pendentes'
                  : filtroStatusProposta === 'APROVADA'
                  ? 'aprovadas'
                  : 'rejeitadas'}
                .
              </Alert>
            ) : (
              <>
                <Box sx={{ mt: 2 }}>
                  {propostasPagina.map((proposta, index) => (
                    <Slide in timeout={400 + index * 100} key={proposta.id}>
                      <Paper
                        elevation={1}
                        sx={{
                          p: { xs: 2, md: 3 },
                          mb: 2.5,
                          borderRadius: 3,
                          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                          transition: 'all .25s',
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            borderColor: alpha(theme.palette.primary.main, 0.3),
                          },
                          touchAction: 'manipulation',
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', sm: 'row' },
                            justifyContent: 'space-between',
                            gap: 2,
                          }}
                        >
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="h6" gutterBottom sx={{ fontWeight: 700 }}>
                              {proposta.titulo}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" paragraph>
                              <strong>Problema:</strong> {proposta.problemaPesquisa}
                            </Typography>
                            <Grid container spacing={1}>
                              <Grid item xs={12} sm={6} md={4}>
                                <Typography variant="body2" color="text.secondary">
                                  <strong>Estudante:</strong> {proposta.estudante?.nome}
                                </Typography>
                              </Grid>
                              <Grid item xs={12} sm={6} md={4}>
                                <Typography variant="body2" color="text.secondary">
                                  <strong>Submetida em:</strong>{' '}
                                  {new Date(proposta.dataSubmissao).toLocaleDateString()}
                                </Typography>
                              </Grid>
                              <Grid item xs={12} sm={6} md={4}>
                                <Typography variant="body2" color="text.secondary">
                                  <strong>Supervisor:</strong>{' '}
                                  {proposta.supervisor?.Usuario?.nome || 'Não designado'}
                                </Typography>
                              </Grid>
                            </Grid>
                            <Box sx={{ mt: 1.5 }}>
                              <Chip
                                label={proposta.status}
                                color={
                                  proposta.status === 'PENDENTE'
                                    ? 'warning'
                                    : proposta.status === 'APROVADA'
                                    ? 'success'
                                    : 'error'
                                }
                                size="small"
                                sx={{ fontWeight: 700 }}
                              />
                            </Box>
                          </Box>

                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: { xs: 'row', sm: 'column' },
                              gap: 1,
                              minWidth: { xs: 'auto', sm: 180 },
                              justifyContent: { xs: 'flex-end', sm: 'flex-start' },
                            }}
                          >
                            {proposta.status === 'PENDENTE' && (
                              <>
                                <Button
                                  variant="contained"
                                  color="primary"
                                  startIcon={<PersonIcon />}
                                  onClick={() => openSupervisorDialog(proposta)}
                                  sx={{ borderRadius: 2, minHeight: 40 }}
                                >
                                  {proposta.supervisorId ? 'Alterar Supervisor' : 'Designar Supervisor'}
                                </Button>
                                <Button
                                  variant="contained"
                                  color="success"
                                  startIcon={<CheckIcon />}
                                  onClick={() => openApproveDialog(proposta)}
                                  disabled={!proposta.supervisorId}
                                  sx={{ borderRadius: 2, minHeight: 40 }}
                                >
                                  Aprovar
                                </Button>
                                <Button
                                  variant="contained"
                                  color="error"
                                  startIcon={<CloseIcon />}
                                  onClick={() => openRejectDialog(proposta)}
                                  sx={{ borderRadius: 2, minHeight: 40 }}
                                >
                                  Rejeitar
                                </Button>
                              </>
                            )}

                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => navigate(`/propostas/${proposta.id}`)}
                              sx={{ borderRadius: 2, minHeight: 40 }}
                            >
                              Ver Detalhes
                            </Button>
                          </Box>
                        </Box>

                        {/* Similaridade — colapsa no mobile */}
                        {proposta.status === 'PENDENTE' && similaridades[proposta.id] && (
                          <ExpandableSection title="Análise de Similaridade" initiallyOpen={false}>
                            {similaridades[proposta.id].length > 0 && (
                              <Grid container spacing={2} sx={{ mb: 2 }}>
                                <Grid item xs={12} sm={4}>
                                  <Paper
                                    sx={{
                                      p: 2,
                                      textAlign: 'center',
                                      bgcolor: alpha(
                                        theme.palette.error.main,
                                        (similaridades[proposta.id][0]?.similaridadeDireta || 0) >=
                                          0.6
                                          ? 0.12
                                          : 0.04
                                      ),
                                    }}
                                  >
                                    <Typography variant="h6">
                                      {(((similaridades[proposta.id][0]?.similaridadeDireta || 0) * 100).toFixed(1))}{' '}
                                      %
                                    </Typography>
                                    <Typography variant="caption">Similaridade Direta</Typography>
                                  </Paper>
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                  <Paper
                                    sx={{
                                      p: 2,
                                      textAlign: 'center',
                                      bgcolor: alpha(
                                        theme.palette.error.main,
                                        (similaridades[proposta.id][0]?.jaroWinkler || 0) >= 0.6
                                          ? 0.12
                                          : 0.04
                                      ),
                                    }}
                                  >
                                    <Typography variant="h6">
                                      {similaridades[proposta.id][0]?.jaroWinkler
                                        ? `${(similaridades[proposta.id][0].jaroWinkler * 100).toFixed(1)}%`
                                        : '0%'}
                                    </Typography>
                                    <Typography variant="caption">Similaridade Jaro-Winkler</Typography>
                                  </Paper>
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                  <Paper
                                    sx={{
                                      p: 2,
                                      textAlign: 'center',
                                      bgcolor: alpha(
                                        theme.palette.error.main,
                                        (similaridades[proposta.id][0]?.similaridade || 0) >= 0.6
                                          ? 0.12
                                          : 0.04
                                      ),
                                    }}
                                  >
                                    <Typography variant="h6">
                                      {(((similaridades[proposta.id][0]?.similaridade || 0) * 100).toFixed(1))}{' '}
                                      %
                                    </Typography>
                                    <Typography variant="caption">Score Combinado</Typography>
                                  </Paper>
                                </Grid>
                              </Grid>
                            )}

                            {similaridades[proposta.id]?.[0]?.similaridade >= 0.6 && (
                              <Alert severity="warning" sx={{ mt: 2 }}>
                                <AlertTitle>Atenção — Alta Similaridade Detectada</AlertTitle>
                                <Typography variant="body2">
                                  {`O problema de pesquisa "${proposta.problemaPesquisa}" apresenta ${(
                                    similaridades[proposta.id][0].similaridade * 100
                                  ).toFixed(1)}% de similaridade com outra proposta existente.`}
                                  {similaridades[proposta.id][0].termosComuns?.length > 0
                                    ? ` Termos em comum: ${similaridades[proposta.id][0].termosComuns.join(', ')}.`
                                    : ''}
                                  {' Recomenda-se uma análise detalhada antes da aprovação.'}
                                </Typography>
                              </Alert>
                            )}

                            {similaridades[proposta.id].length > 0 && (
                              <Box sx={{ mt: 2 }}>
                                <Typography variant="subtitle2" gutterBottom>
                                  Propostas Similares Encontradas
                                </Typography>
                                <Table size="small">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell>Título</TableCell>
                                      <TableCell>Problema de Pesquisa</TableCell>
                                      <TableCell>Estudante</TableCell>
                                      <TableCell>Data de Submissão</TableCell>
                                      <TableCell align="right">Grau de Similaridade</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {similaridades[proposta.id].map((similar) => (
                                      <TableRow key={similar.propostaId}>
                                        <TableCell>
                                          {similar.propostaOriginal?.titulo || 'Título indisponível'}
                                        </TableCell>
                                        <TableCell>
                                          {similar.propostaOriginal?.problemaPesquisa || '—'}
                                        </TableCell>
                                        <TableCell>
                                          {similar.propostaOriginal?.estudante || '—'}
                                        </TableCell>
                                        <TableCell>
                                          {similar.propostaOriginal?.dataSubmissao
                                            ? new Date(
                                                similar.propostaOriginal.dataSubmissao
                                              ).toLocaleDateString()
                                            : '—'}
                                        </TableCell>
                                        <TableCell align="right">
                                          {typeof similar.similaridade === 'number'
                                            ? `${(similar.similaridade * 100).toFixed(1)}%`
                                            : 'N/A'}
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </Box>
                            )}
                          </ExpandableSection>
                        )}
                      </Paper>
                    </Slide>
                  ))}
                </Box>

                {totalPagesPropostas > 1 && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                    <Pagination
                      color="primary"
                      count={totalPagesPropostas}
                      page={pagePropostas}
                      onChange={(_, newPage) => setPagePropostas(newPage)}
                      size="large"
                      sx={{ '& .MuiPaginationItem-root': { borderRadius: 2 } }}
                    />
                  </Box>
                )}
              </>
            )}
          </Box>
        </CardContent>
      </Card>
    </Fade>
  );

  // ---------- PROJETOS ----------
  const renderProjetos = () => (
    <Fade in timeout={600}>
      <Card elevation={2} sx={{ borderRadius: 3 }}>
        <CardContent sx={{ p: { xs: 2, md: 4 } }}>
          <Box sx={{ mb: 4 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: { xs: 'stretch', sm: 'center' },
                gap: 1,
                mb: 3,
                pb: 2,
                borderBottom: `2px solid ${alpha(theme.palette.success.main, 0.1)}`,
                flexWrap: 'wrap',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <AssignmentIcon sx={{ fontSize: 28, color: theme.palette.success.main, mr: 1.5 }} />
                <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.success.main }}>
                  Gestão de Projectos
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button
                  variant={filtroStatusProjeto === 'PENDENTE' ? 'contained' : 'outlined'}
                  onClick={() => {
                    setFiltroStatusProjeto('PENDENTE');
                    setPageProjetos(1);
                  }}
                  size="small"
                  sx={{ borderRadius: 2, minHeight: 40 }}
                >
                  Pendentes ({projetos.filter((p) => p.status === 'PENDENTE').length})
                </Button>
                <Button
                  variant={filtroStatusProjeto === 'EM_REVISAO' ? 'contained' : 'outlined'}
                  onClick={() => {
                    setFiltroStatusProjeto('EM_REVISAO');
                    setPageProjetos(1);
                  }}
                  size="small"
                  color="info"
                  sx={{ borderRadius: 2, minHeight: 40 }}
                >
                  Em Revisão ({projetos.filter((p) => p.status === 'EM_REVISAO').length})
                </Button>
                <Button
                  variant={filtroStatusProjeto === 'REVISADO' ? 'contained' : 'outlined'}
                  onClick={() => {
                    setFiltroStatusProjeto('REVISADO');
                    setPageProjetos(1);
                  }}
                  size="small"
                  color="warning"
                  sx={{ borderRadius: 2, minHeight: 40 }}
                >
                  Revisados ({projetos.filter((p) => p.status === 'REVISADO').length})
                </Button>
                <Button
                  variant={filtroStatusProjeto === 'APROVADO' ? 'contained' : 'outlined'}
                  onClick={() => {
                    setFiltroStatusProjeto('APROVADO');
                    setPageProjetos(1);
                  }}
                  size="small"
                  color="success"
                  sx={{ borderRadius: 2, minHeight: 40 }}
                >
                  Aprovados ({projetos.filter((p) => p.status === 'APROVADO').length})
                </Button>
              </Box>
            </Box>

            {projetosFiltrados.length === 0 ? (
              <Alert
                severity="info"
                sx={{
                  borderRadius: 2,
                  border: `1px solid ${alpha(theme.palette.info.main, 0.28)}`,
                  bgcolor: alpha(theme.palette.info.main, 0.06),
                }}
              >
                Não há projetos {filtroStatusProjeto.toLowerCase().replace('_', ' ')} no momento.
              </Alert>
            ) : (
              <>
                <Box sx={{ mt: 2 }}>
                  {projetosPagina.map((projeto, index) => (
                    <Slide in timeout={400 + index * 100} key={projeto.id}>
                      <Paper
                        elevation={1}
                        sx={{
                          p: { xs: 2, md: 3 },
                          mb: 2.5,
                          borderRadius: 3,
                          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                          transition: 'all .25s',
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            borderColor: alpha(theme.palette.success.main, 0.3),
                          },
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', sm: 'row' },
                            justifyContent: 'space-between',
                            gap: 2,
                          }}
                        >
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="h6" gutterBottom sx={{ fontWeight: 700 }}>
                              {projeto.proposta?.titulo}
                            </Typography>
                            <Grid container spacing={1}>
                              <Grid item xs={12} sm={6} md={4}>
                                <Typography variant="body2" color="text.secondary">
                                  <strong>Estudante:</strong> {projeto.proposta?.estudante?.nome}
                                </Typography>
                              </Grid>
                              <Grid item xs={12} sm={6} md={4}>
                                <Typography variant="body2" color="text.secondary">
                                  <strong>Supervisor:</strong> {projeto.supervisor?.Usuario?.nome}
                                </Typography>
                              </Grid>
                              <Grid item xs={12} sm={6} md={4}>
                                <Typography variant="body2" color="text.secondary">
                                  <strong>Revisor:</strong> {projeto.revisor?.nome || 'Não designado'}
                                </Typography>
                              </Grid>
                              <Grid item xs={12} sm={6} md={4}>
                                <Typography variant="body2" color="text.secondary">
                                  <strong>Submetido em:</strong>{' '}
                                  {new Date(projeto.created_at).toLocaleDateString()}
                                </Typography>
                              </Grid>
                            </Grid>

                            <Box sx={{ display: 'flex', gap: 1, mt: 1.5, flexWrap: 'wrap' }}>
                              <Chip
                                label={projeto.status}
                                color={
                                  projeto.status === 'PENDENTE'
                                    ? 'warning'
                                    : projeto.status === 'APROVADO'
                                    ? 'success'
                                    : 'info'
                                }
                                size="small"
                                sx={{ fontWeight: 700 }}
                              />
                              {projeto.status === 'APROVADO' && projeto.credencial_emitida && (
                                <Chip
                                  label={`Credencial: ${projeto.numero_credencial || 'Emitida'}`}
                                  color="success"
                                  variant="outlined"
                                  size="small"
                                />
                              )}
                            </Box>

                            {projeto.parecer_revisor && (
                              <Typography variant="body2" sx={{ mt: 1 }}>
                                <strong>Parecer do Revisor:</strong>{' '}
                                {`${projeto.parecer_revisor.substring(0, 100)}...`}
                              </Typography>
                            )}
                          </Box>

                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: { xs: 'row', sm: 'column' },
                              gap: 1,
                              minWidth: { xs: 'auto', sm: 180 },
                              justifyContent: { xs: 'flex-end', sm: 'flex-start' },
                            }}
                          >
                            {projeto.status === 'PENDENTE' && (
                              <Button
                                variant="contained"
                                color="primary"
                                startIcon={<PersonIcon />}
                                onClick={() => openProjetoRevisorDialog(projeto)}
                                sx={{ borderRadius: 2, minHeight: 40 }}
                              >
                                Encaminhar para Revisor
                              </Button>
                            )}
                            {projeto.status === 'REVISADO' && (
                              <Button
                                variant="contained"
                                color="success"
                                startIcon={<BadgeIcon />}
                                onClick={() => handleEmitirCredencial(projeto)}
                                sx={{ borderRadius: 2, minHeight: 40 }}
                              >
                                Emitir Credencial
                              </Button>
                            )}
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => navigate(`/projetos/${projeto.id}`)}
                              sx={{ borderRadius: 2, minHeight: 40 }}
                            >
                              Ver Detalhes
                            </Button>
                          </Box>
                        </Box>

                        {/* Similaridade — colapsa no mobile */}
                        {projeto.status === 'PENDENTE' &&
                          similaridadesProjetos[projeto.id] &&
                          similaridadesProjetos[projeto.id].length > 0 && (
                            <ExpandableSection title="Alerta de Similaridade" initiallyOpen={false}>
                              <Alert severity="warning" sx={{ borderRadius: 2 }}>
                                <AlertTitle>Alerta de Similaridade</AlertTitle>
                                <Typography variant="body2">
                                  Este projeto apresenta{' '}
                                  {`${(similaridadesProjetos[projeto.id][0].similaridade * 100).toFixed(1)}%`}{' '}
                                  de similaridade com outro projecto existente.
                                  {projeto.status === 'PENDENTE' &&
                                    similaridadesProjetos[projeto.id][0].termosComuns?.length > 0 &&
                                    ` Termos em comum: ${similaridadesProjetos[
                                      projeto.id
                                    ][0].termosComuns
                                      .slice(0, 5)
                                      .join(', ')}...`}
                                </Typography>
                                <Button
                                  size="small"
                                  sx={{ mt: 1 }}
                                  onClick={() => navigate(`/projetos/${projeto.id}`)}
                                >
                                  Ver detalhes
                                </Button>
                              </Alert>
                            </ExpandableSection>
                          )}
                      </Paper>
                    </Slide>
                  ))}
                </Box>

                {totalPagesProjetos > 1 && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                    <Pagination
                      color="primary"
                      count={totalPagesProjetos}
                      page={pageProjetos}
                      onChange={(_, newPage) => setPageProjetos(newPage)}
                      size="large"
                      sx={{ '& .MuiPaginationItem-root': { borderRadius: 2 } }}
                    />
                  </Box>
                )}
              </>
            )}
          </Box>
        </CardContent>
      </Card>
    </Fade>
  );

  // ---------- MONOGRAFIAS ----------
  const renderMonografias = () => (
    <Fade in timeout={600}>
      <Card elevation={2} sx={{ borderRadius: 3 }}>
        <CardContent sx={{ p: { xs: 2, md: 4 } }}>
          <Box sx={{ mb: 4 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: { xs: 'stretch', sm: 'center' },
                gap: 1,
                mb: 3,
                pb: 2,
                borderBottom: `2px solid ${alpha(theme.palette.warning.main, 0.1)}`,
                flexWrap: 'wrap',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <DescriptionIcon sx={{ fontSize: 28, color: theme.palette.warning.main, mr: 1.5 }} />
                <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.warning.main }}>
                  Gestão de Monografias
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button
                  variant={filtroStatusMonografia === 'RASCUNHO' ? 'contained' : 'outlined'}
                  onClick={() => {
                    setFiltroStatusMonografia('RASCUNHO');
                    setPageMonografias(1);
                  }}
                  size="small"
                  sx={{ borderRadius: 2, minHeight: 40 }}
                >
                  Rascunhos ({monografias.filter((m) => m.status === 'RASCUNHO').length})
                </Button>
                <Button
                  variant={filtroStatusMonografia === 'SUBMETIDA' ? 'contained' : 'outlined'}
                  onClick={() => {
                    setFiltroStatusMonografia('SUBMETIDA');
                    setPageMonografias(1);
                  }}
                  size="small"
                  color="info"
                  sx={{ borderRadius: 2, minHeight: 40 }}
                >
                  Submetidas (
                  {
                    monografias.filter(
                      (m) =>
                        m.status === 'SUBMETIDA' ||
                        m.status === 'SUBMETIDA_A_DIRECCAO_CIENTIFICA'
                    ).length
                  }
                  )
                </Button>
                <Button
                  variant={filtroStatusMonografia === 'EM_REVISAO' ? 'contained' : 'outlined'}
                  onClick={() => {
                    setFiltroStatusMonografia('EM_REVISAO');
                    setPageMonografias(1);
                  }}
                  size="small"
                  color="warning"
                  sx={{ borderRadius: 2, minHeight: 40 }}
                >
                  Em Revisão ({monografias.filter((m) => m.status === 'EM_REVISAO').length})
                </Button>
                <Button
                  variant={filtroStatusMonografia === 'REVISTO' ? 'contained' : 'outlined'}
                  onClick={() => {
                    setFiltroStatusMonografia('REVISTO');
                    setPageMonografias(1);
                  }}
                  size="small"
                  color="secondary"
                  sx={{ borderRadius: 2, minHeight: 40 }}
                >
                  Revistas ({monografias.filter((m) => m.status === 'REVISTO').length})
                </Button>
                <Button
                  variant={filtroStatusMonografia === 'DEVOLVIDA' ? 'contained' : 'outlined'}
                  onClick={() => {
                    setFiltroStatusMonografia('DEVOLVIDA');
                    setPageMonografias(1);
                  }}
                  size="small"
                  color="error"
                  sx={{ borderRadius: 2, minHeight: 40 }}
                >
                  Devolvidas ({monografias.filter((m) => m.status === 'DEVOLVIDA').length})
                </Button>
                <Button
                  variant={filtroStatusMonografia === 'APROVADA' ? 'contained' : 'outlined'}
                  onClick={() => {
                    setFiltroStatusMonografia('APROVADA');
                    setPageMonografias(1);
                  }}
                  size="small"
                  color="success"
                  sx={{ borderRadius: 2, minHeight: 40 }}
                >
                  Aprovadas ({monografias.filter((m) => m.status === 'APROVADA').length})
                </Button>
              </Box>
            </Box>

            {monografiasFiltradas.length === 0 ? (
              <Alert
                severity="info"
                sx={{
                  borderRadius: 2,
                  border: `1px solid ${alpha(theme.palette.info.main, 0.28)}`,
                  bgcolor: alpha(theme.palette.info.main, 0.06),
                }}
              >
                Não há monografias {filtroStatusMonografia.toLowerCase().replace('_', ' ')} no momento.
              </Alert>
            ) : (
              <>
                <Box sx={{ mt: 2 }}>
                  {monografiasPagina.map((monografia, index) => (
                    <Slide in timeout={400 + index * 100} key={monografia.id}>
                      <Paper
                        elevation={1}
                        sx={{
                          p: { xs: 2, md: 3 },
                          mb: 2.5,
                          borderRadius: 3,
                          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                          transition: 'all .25s',
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            borderColor: alpha(theme.palette.warning.main, 0.3),
                          },
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', sm: 'row' },
                            justifyContent: 'space-between',
                            gap: 2,
                          }}
                        >
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="h6" gutterBottom sx={{ fontWeight: 700 }}>
                              {monografia.projeto?.proposta?.titulo ||
                                monografia.titulo ||
                                `Monografia #${monografia.id}`}
                            </Typography>

                            <Grid container spacing={1}>
                              <Grid item xs={12} sm={6} md={4}>
                                <Typography variant="body2" color="text.secondary">
                                  <strong>Estudante:</strong>{' '}
                                  {monografia.projeto?.proposta?.estudante?.nome || 'Não informado'}
                                </Typography>
                              </Grid>
                              <Grid item xs={12} sm={6} md={4}>
                                <Typography variant="body2" color="text.secondary">
                                  <strong>Supervisor:</strong>{' '}
                                  {monografia.projeto?.supervisor?.Usuario?.nome || 'Não informado'}
                                </Typography>
                              </Grid>
                              <Grid item xs={12} sm={6} md={4}>
                                <Typography variant="body2" color="text.secondary">
                                  <strong>Versão:</strong> {monografia.versao_documento || 'Não informado'}
                                </Typography>
                              </Grid>
                              <Grid item xs={12} sm={6} md={4}>
                                <Typography variant="body2" color="text.secondary">
                                  <strong>Data de Submissão:</strong>{' '}
                                  {monografia.data_submissao
                                    ? new Date(monografia.data_submissao).toLocaleDateString()
                                    : 'Não informado'}
                                </Typography>
                              </Grid>
                            </Grid>

                            <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
                              <Chip
                                label={monografia.status}
                                color={
                                  monografia.status === 'PENDENTE' || monografia.status === 'SUBMETIDA'
                                    ? 'warning'
                                    : monografia.status === 'EM_REVISAO'
                                    ? 'info'
                                    : 'success'
                                }
                                size="small"
                                sx={{ fontWeight: 700 }}
                              />
                            </Box>

                            {/* Parecer no card (REVISTO) */}
                            {monografia.status === 'REVISTO' && monografia.parecer_revisor && (
                              <Box
                                sx={{
                                  mt: 2,
                                  p: 2,
                                  bgcolor: alpha(theme.palette.primary.main, 0.05),
                                  borderRadius: 2,
                                  borderLeft: '4px solid',
                                  borderColor: 'primary.main',
                                }}
                              >
                                <Typography variant="subtitle2" gutterBottom color="primary">
                                  Parecer do Revisor:
                                </Typography>
                                <Typography variant="body2">
                                  {monografia.parecer_revisor.length > 150
                                    ? `${monografia.parecer_revisor.substring(0, 150)}...`
                                    : monografia.parecer_revisor}
                                </Typography>
                              </Box>
                            )}
                          </Box>

                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: { xs: 'row', sm: 'column' },
                              gap: 1,
                              minWidth: { xs: 'auto', sm: 200 },
                              justifyContent: { xs: 'flex-end', sm: 'flex-start' },
                            }}
                          >
                            {(monografia.status === 'RASCUNHO' ||
                              monografia.status === 'SUBMETIDA' ||
                              monografia.status === 'SUBMETIDA_A_DIRECCAO_CIENTIFICA') && (
                              <Button
                                variant="contained"
                                color="primary"
                                startIcon={<PersonIcon />}
                                onClick={() => openMonografiaRevisorDialog(monografia)}
                                sx={{ borderRadius: 2, minHeight: 40 }}
                              >
                                Encaminhar para Revisão
                              </Button>
                            )}

                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => navigate(`/monografias/${monografia.id}`)}
                              sx={{ borderRadius: 2, minHeight: 40 }}
                            >
                              Ver Detalhes
                            </Button>

                            {monografia.status === 'REVISTO' && (
                              <>
                                <Button
                                  variant="contained"
                                  color="success"
                                  onClick={() => handleAprovarMonografia(monografia.id)}
                                  sx={{ borderRadius: 2, minHeight: 40 }}
                                >
                                  Aprovar Monografia
                                </Button>
                                <Button
                                  variant="outlined"
                                  color="error"
                                  onClick={() => handleDevolverMonografia(monografia.id)}
                                  sx={{ borderRadius: 2, minHeight: 40 }}
                                >
                                  Devolver para Correção
                                </Button>
                                <Button
                                  variant="text"
                                  size="small"
                                  onClick={() => handleVerParecerMonografia(monografia)}
                                  sx={{ borderRadius: 2, minHeight: 40 }}
                                >
                                  Ver Parecer
                                </Button>
                              </>
                            )}

                            {monografia.status === 'EM_REVISAO' && (
                              <>
                                <Button
                                  variant="contained"
                                  color="success"
                                  onClick={() => handleAprovarMonografia(monografia.id)}
                                  sx={{ borderRadius: 2, minHeight: 40 }}
                                >
                                  Aprovar
                                </Button>
                                <Button
                                  variant="outlined"
                                  color="error"
                                  onClick={() => handleDevolverMonografia(monografia.id)}
                                  sx={{ borderRadius: 2, minHeight: 40 }}
                                >
                                  Devolver
                                </Button>
                              </>
                            )}
                          </Box>
                        </Box>

                        {/* Similaridade — colapsa no mobile */}
                        {(monografia.status === 'PENDENTE' ||
                          monografia.status === 'SUBMETIDA' ||
                          monografia.status === 'SUBMETIDA_A_DIRECCAO_CIENTIFICA') &&
                          similaridadesMonografias[monografia.id] &&
                          similaridadesMonografias[monografia.id].length > 0 &&
                          similaridadesMonografias[monografia.id][0].similaridade >= 0.4 && (
                            <ExpandableSection title="Alerta de Similaridade" initiallyOpen={false}>
                              <Alert severity="warning" sx={{ borderRadius: 2 }}>
                                <AlertTitle>Alerta de Similaridade</AlertTitle>
                                <Typography variant="body2">
                                  Esta monografia apresenta{' '}
                                  {`${(similaridadesMonografias[monografia.id][0].similaridade * 100).toFixed(1)}%`}{' '}
                                  de similaridade com{' '}
                                  {similaridadesMonografias[monografia.id][0].tipo === 'arquivo_digital'
                                    ? 'um documento do repositório'
                                    : 'outra monografia'}
                                  .
                                  {similaridadesMonografias[monografia.id][0].termosComuns?.length > 0 &&
                                    ` Termos em comum: ${similaridadesMonografias[
                                      monografia.id
                                    ][0].termosComuns
                                      .slice(0, 5)
                                      .join(', ')}...`}
                                </Typography>
                                <Button
                                  size="small"
                                  sx={{ mt: 1 }}
                                  onClick={() => navigate(`/monografias/${monografia.id}`)}
                                >
                                  Ver detalhes completos
                                </Button>
                              </Alert>
                            </ExpandableSection>
                          )}
                      </Paper>
                    </Slide>
                  ))}
                </Box>

                {totalPagesMonografias > 1 && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                    <Pagination
                      color="primary"
                      count={totalPagesMonografias}
                      page={pageMonografias}
                      onChange={(_, newPage) => setPageMonografias(newPage)}
                      size="large"
                      sx={{ '& .MuiPaginationItem-root': { borderRadius: 2 } }}
                    />
                  </Box>
                )}

                {/* Histórico */}
                <Box sx={{ mt: 5 }}>
                  <Paper
                    elevation={1}
                    sx={{
                      p: { xs: 2, md: 3 },
                      borderRadius: 3,
                      background: `linear-gradient(135deg, ${alpha(
                        theme.palette.secondary.main,
                        0.05
                      )} 0%, ${alpha(theme.palette.secondary.main, 0.02)} 100%)`,
                      border: `1px solid ${alpha(theme.palette.secondary.main, 0.2)}`,
                    }}
                  >
                    <Typography
                      variant="h6"
                      gutterBottom
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        color: theme.palette.secondary.main,
                        fontWeight: 700,
                      }}
                    >
                      <DescriptionIcon sx={{ mr: 1 }} />
                  
    Monografias Revistas ({monografiasHistorico.length})
                    </Typography>

                   {monografiasHistorico.length > 0 ? (
  monografiasHistorico.map((monografia, index) => (
    <Slide in timeout={300 + index * 100} key={monografia.id}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, md: 3 },
          mb: 2,
          borderRadius: 2,
          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          transition: 'all .25s',
          '&:hover': {
            transform: 'translateX(4px)',
            borderColor: alpha(theme.palette.secondary.main, 0.3),
          },
        }}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={8}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              {monografia.titulo ||
                monografia.projeto?.proposta?.titulo ||
                `Monografia #${monografia.id}`}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Estudante:{' '}
              {monografia.projeto?.proposta?.estudante?.nome || 'Não informado'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
            Revista em:{' '}
              {monografia.data_revisao
                ? new Date(monografia.data_revisao).toLocaleDateString()
                : 'Não informado'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              <strong>Parecer do Revisor:</strong>{' '}
              {monografia.parecer_revisor
                ? monografia.parecer_revisor.length > 100
                  ? `${monografia.parecer_revisor.substring(0, 100)}...`
                  : monografia.parecer_revisor
                : 'Sem parecer'}
            </Typography>
          </Grid>

          <Grid
            item
            xs={12}
            sm={4}
            sx={{
              display: 'flex',
              justifyContent: { xs: 'flex-start', sm: 'flex-end' },
              alignItems: 'center',
              gap: 1,
              flexWrap: 'wrap',
            }}
          >
            <Button
              variant="outlined"
              size="small"
              onClick={() => navigate(`/monografias/${monografia.id}`)}
              sx={{ borderRadius: 2, minHeight: 40 }}
            >
              Ver Detalhes
            </Button>
            {monografia.parecer_revisor && (
              <Button
                variant="text"
                size="small"
                onClick={() => handleVerParecerMonografia(monografia)}
                sx={{ borderRadius: 2, minHeight: 40 }}
              >
                Ver Parecer
              </Button>
            )}
          </Grid>
        </Grid>
      </Paper>
    </Slide>
  ))
) : (
  <Alert severity="info" sx={{ borderRadius: 2 }}>
    Não há monografias revisadas no histórico.
  </Alert>
)}

                  </Paper>
                </Box>
              </>
            )}
          </Box>
        </CardContent>

        {/* Modal Parecer Completo */}
        <Dialog
          open={parecerModalOpen}
          onClose={() => setParecerModalOpen(false)}
          maxWidth="md"
          fullWidth
          fullScreen={fullScreenDialog}
        >
          <DialogTitle
            sx={{
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              color: theme.palette.primary.main,
            }}
          >
            Parecer Completo do Revisor
          </DialogTitle>
          <DialogContent sx={{ p: 3 }}>
            <Typography
              variant="body1"
              component="pre"
              sx={{
                whiteSpace: 'pre-wrap',
                fontFamily: 'inherit',
                p: 2,
                bgcolor: alpha(theme.palette.grey[50], 0.5),
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
              }}
            >
              {selectedParecer}
            </Typography>
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button onClick={() => setParecerModalOpen(false)} variant="contained" sx={{ borderRadius: 2 }}>
              Fechar
            </Button>
          </DialogActions>
        </Dialog>
      </Card>
    </Fade>
  );

  // ---------- DEFESAS ----------
  const renderDefesas = () => (
    <Fade in timeout={600}>
      <Card elevation={2} sx={{ borderRadius: 3 }}>
        <CardContent sx={{ p: { xs: 2, md: 4 } }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              mb: 3,
              pb: 2,
              borderBottom: `2px solid ${alpha(theme.palette.error.main, 0.1)}`,
            }}
          >
            <EventNoteIcon sx={{ fontSize: 28, color: theme.palette.error.main, mr: 1.5 }} />
            <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.error.main }}>
              Defesas Agendadas
            </Typography>
          </Box>

          <Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>}>
            <DefesaList />
          </Suspense>
        </CardContent>
      </Card>
    </Fade>
  );

  // ---------- AVALIAÇÕES ----------
  const RenderAvaliacoes = () => {
    const [estatisticas, setEstatisticas] = useState(null);

    useEffect(() => {
      let alive = true;
      (async () => {
        try {
          const data = await ChefeDepartamentoService.getEstatisticasAvaliacoes();
          if (alive) setEstatisticas(data);
        } catch (err) {
          console.error('Erro ao carregar estatísticas:', err);
        }
      })();
      return () => {
        alive = false;
      };
    }, []);

    if (!estatisticas)
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress size={60} />
        </Box>
      );

    return (
      <Fade in timeout={600}>
        <Card elevation={2} sx={{ borderRadius: 3 }}>
          <CardContent sx={{ p: { xs: 2, md: 4 } }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                mb: 3,
                pb: 2,
                borderBottom: `2px solid ${alpha(theme.palette.secondary.main, 0.1)}`,
              }}
            >
              <VisibilityIcon sx={{ fontSize: 28, color: theme.palette.secondary.main, mr: 1.5 }} />
              <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.secondary.main }}>
                Avaliações de Defesas
              </Typography>
            </Box>

            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12} md={4}>
                <Paper
                  elevation={1}
                  sx={{
                    p: 3,
                    textAlign: 'center',
                    borderRadius: 3,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                    color: 'white',
                  }}
                >
                  <Typography variant="h3" sx={{ fontWeight: 800, mb: 0.5 }}>
                    {estatisticas.total}
                  </Typography>
                  <Typography variant="h6">Total de Avaliações</Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} md={4}>
                <Paper
                  elevation={1}
                  sx={{
                    p: 3,
                    textAlign: 'center',
                    borderRadius: 3,
                    background: `linear-gradient(135deg, ${theme.palette.success.main} 0%, ${theme.palette.success.dark} 100%)`,
                    color: 'white',
                  }}
                >
                  <Typography variant="h3" sx={{ fontWeight: 800, mb: 0.5 }}>
                    {estatisticas.estatisticas.concluidas}
                  </Typography>
                  <Typography variant="h6">Avaliações Concluídas</Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} md={4}>
                <Paper
                  elevation={1}
                  sx={{
                    p: 3,
                    textAlign: 'center',
                    borderRadius: 3,
                    background: `linear-gradient(135deg, ${theme.palette.warning.main} 0%, ${theme.palette.warning.dark} 100%)`,
                    color: 'white',
                  }}
                >
                  <Typography variant="h3" sx={{ fontWeight: 800, mb: 0.5 }}>
                    {estatisticas.estatisticas.mediaGeral}
                  </Typography>
                  <Typography variant="h6">Média Geral</Typography>
                </Paper>
              </Grid>
            </Grid>

            <Paper elevation={1} sx={{ p: 3, mb: 4, borderRadius: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', fontWeight: 700 }}>
                <AnalyticsIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
                Distribuição das Notas
              </Typography>
              {Object.entries(estatisticas.estatisticas?.distribuicaoNotas || {}).map(([faixa, quantidade]) => (
                <Box key={faixa} sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {faixa.charAt(0).toUpperCase() + faixa.slice(1)}
                    </Typography>
                    <Typography variant="caption" sx={{ fontWeight: 700 }}>
                      {quantidade} avaliações ({((quantidade / estatisticas.total) * 100).toFixed(1)}%)
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={(quantidade / estatisticas.total) * 100}
                    sx={{
                      height: 10,
                      borderRadius: 5,
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      '& .MuiLinearProgress-bar': { borderRadius: 5 },
                    }}
                  />
                </Box>
              ))}
            </Paper>

            <Grid container spacing={3}>
              {estatisticas.avaliacoes?.map((avaliacao, index) => (
                <Grid item xs={12} sm={6} md={4} key={avaliacao.id}>
                  <Slide in timeout={300 + index * 100}>
                    <Card
                      elevation={2}
                      sx={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        borderRadius: 3,
                        transition: 'all .25s',
                        '&:hover': { transform: 'translateY(-6px)' },
                      }}
                    >
                      <CardContent sx={{ flexGrow: 1, p: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                          <ArticleIcon sx={{ mr: 1, color: 'primary.main', fontSize: 24 }} />
                          <Typography variant="h6" sx={{ fontWeight: 700 }}>
                            Defesa {avaliacao.defesaId}
                          </Typography>
                        </Box>
                        <Typography variant="h4" gutterBottom sx={{ color: 'primary.main', fontWeight: 800 }}>
                          Nota: {avaliacao.mediaFinal?.toFixed(2)}
                        </Typography>
                        <Box sx={{ mt: 1.5, p: 2, bgcolor: alpha(theme.palette.grey[50], 0.5), borderRadius: 2 }}>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                            <strong>Nota Arguente:</strong> {avaliacao.arguenteTotal?.toFixed(2)}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                            <strong>Nota Presidente:</strong> {avaliacao.presidenteTotal?.toFixed(2)}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            <strong>Nota Supervisor:</strong> {avaliacao.supervisorTotal?.toFixed(2)}
                          </Typography>
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
                          Atualizado em: {new Date(avaliacao.updatedAt).toLocaleString()}
                        </Typography>
                      </CardContent>
                      <Box sx={{ p: 2, mt: 'auto' }}>
                        <Button
                          variant="contained"
                          fullWidth
                          startIcon={<VisibilityIcon />}
                          onClick={() => navigate(`/avaliacoes/${avaliacao.id}`)}
                          sx={{ borderRadius: 2, fontWeight: 700, minHeight: 44 }}
                        >
                          Ver Detalhes
                        </Button>
                      </Box>
                    </Card>
                  </Slide>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      </Fade>
    );
  };
  const renderAvaliacoes = () => <RenderAvaliacoes />;

  // ---------- ARQUIVAMENTO ----------
  const RenderArquivamento = () => {
    const [filtroStatusArquivamento, setFiltroStatusArquivamento] = useState('PENDENTE');

    const monografiasArquivaveis = monografias.filter(
      (m) => m.status === 'APROVADA' && !m.arquivada
    );
    const monografiasArquivadas = monografias.filter((m) => m.arquivada === true);
    const monografiasFiltradas =
      filtroStatusArquivamento === 'PENDENTE' ? monografiasArquivaveis : monografiasArquivadas;

    return (
      <Fade in timeout={600}>
        <Card elevation={2} sx={{ borderRadius: 3 }}>
          <CardContent sx={{ p: { xs: 2, md: 4 } }}>
            <Box sx={{ mb: 3 }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: { xs: 'stretch', sm: 'center' },
                  gap: 1,
                  mb: 2,
                  pb: 2,
                  borderBottom: `2px solid ${alpha(theme.palette.grey[600], 0.1)}`,
                  flexWrap: 'wrap',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <ArchiveIcon sx={{ fontSize: 28, color: theme.palette.grey[600], mr: 1.5 }} />
                  <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.grey[700] }}>
                    Administrar Arquivamentos de Monografias
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Button
                    variant={filtroStatusArquivamento === 'PENDENTE' ? 'contained' : 'outlined'}
                    onClick={() => setFiltroStatusArquivamento('PENDENTE')}
                    size="small"
                    sx={{ borderRadius: 2, minHeight: 40 }}
                  >
                    Pendentes de Arquivamento ({monografiasArquivaveis.length})
                  </Button>
                  <Button
                    variant={filtroStatusArquivamento === 'ARQUIVADA' ? 'contained' : 'outlined'}
                    onClick={() => setFiltroStatusArquivamento('ARQUIVADA')}
                    size="small"
                    color="success"
                    sx={{ borderRadius: 2, minHeight: 40 }}
                  >
                    Arquivadas ({monografiasArquivadas.length})
                  </Button>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={() => fetchDados()}
                  size="small"
                  sx={{ borderRadius: 2, minHeight: 40 }}
                >
                  Atualizar Dados
                </Button>
              </Box>

              <Alert
                severity="info"
                sx={{
                  mb: 3,
                  borderRadius: 2,
                  border: `1px solid ${alpha(theme.palette.info.main, 0.28)}`,
                  bgcolor: alpha(theme.palette.info.main, 0.06),
                }}
              >
                <Typography variant="body2">
                  {filtroStatusArquivamento === 'PENDENTE'
                    ? 'Monografias aprovadas que podem ser arquivadas no repositório digital.'
                    : 'Monografias que já foram arquivadas no repositório digital.'}
                </Typography>
              </Alert>

              {monografiasFiltradas.length === 0 ? (
                <Alert severity="info" sx={{ borderRadius: 2 }}>
                  {filtroStatusArquivamento === 'PENDENTE'
                    ? 'Não há monografias pendentes de arquivamento.'
                    : 'Não há monografias arquivadas no repositório.'}
                </Alert>
              ) : (
                <Grid container spacing={3}>
                  {monografiasFiltradas.map((monografia, index) => (
                    <Grid item xs={12} md={6} key={monografia.id}>
                      <Slide in timeout={300 + index * 100}>
                        <Paper
                          elevation={2}
                          sx={{
                            p: 3,
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            borderRadius: 3,
                            transition: 'all .25s',
                            '&:hover': { transform: 'translateY(-6px)' },
                          }}
                        >
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="h6" gutterBottom sx={{ fontWeight: 700 }}>
                              {monografia.titulo || `Monografia #${monografia.id}`}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              <strong>Estudante:</strong> {monografia.estudante?.nome}
                            </Typography>
                            {monografia.projeto?.supervisor?.Usuario && (
                              <Typography variant="body2" color="text.secondary">
                                <strong>Supervisor:</strong> {monografia.projeto.supervisor.Usuario.nome}
                              </Typography>
                            )}
                            {monografia.defesa?.data_defesa && (
                              <Typography variant="body2" color="text.secondary">
                                <strong>Data da Defesa:</strong>{' '}
                                {new Date(monografia.defesa.data_defesa).toLocaleDateString()}
                              </Typography>
                            )}
                            {filtroStatusArquivamento === 'ARQUIVADA' && monografia.data_arquivamento && (
                              <Typography variant="body2" color="text.secondary">
                                <strong>Arquivada em:</strong>{' '}
                                {new Date(monografia.data_arquivamento).toLocaleDateString()}
                              </Typography>
                            )}
                            <Box sx={{ mt: 1.5 }}>
                              <Chip label={monografia.status} color="success" size="small" sx={{ fontWeight: 700 }} />
                            </Box>
                          </Box>

                          <Box sx={{ mt: 'auto', display: 'flex', gap: 1, flexDirection: { xs: 'column', sm: 'row' } }}>
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => navigate(`/monografias/${monografia.id}`)}
                              sx={{ borderRadius: 2, minHeight: 40 }}
                            >
                              Ver Detalhes
                            </Button>

                            {filtroStatusArquivamento === 'PENDENTE' ? (
                              <Suspense fallback={<Button disabled>Carregando…</Button>}>
                                <ArquivarMonografia
                                  monografia={monografia}
                                  onSuccess={() => {
                                    showToast('Monografia arquivada com sucesso!', 'success');
                                    fetchDados();
                                  }}
                                />
                              </Suspense>
                            ) : (
                              <Button
                                variant="outlined"
                                color="primary"
                                size="small"
                                onClick={async () => {
                                  try {
                                    const arquivoDigitalService = await import(
                                      '../../services/arquivoDigitalService'
                                    );
                                    const arquivos = await arquivoDigitalService.listarArquivosDigitais();
                                    const arquivo = arquivos.find((a) => a.monografia_id === monografia.id);
                                    if (arquivo) {
                                      navigate(`/repositorio?id=${arquivo.id}`);
                                    } else {
                                      showToast('Não foi possível encontrar o arquivo desta monografia no repositório', 'error');
                                    }
                                  } catch (error) {
                                    console.error('Erro ao acessar o repositório:', error);
                                    showToast('Erro ao tentar acessar o repositório. Por favor, tente novamente.', 'error');
                                  }
                                }}
                                startIcon={<VisibilityIcon />}
                                sx={{ borderRadius: 2, minHeight: 40 }}
                              >
                                Ver no Repositório
                              </Button>
                            )}
                          </Box>
                        </Paper>
                      </Slide>
                    </Grid>
                  ))}
                </Grid>
              )}
            </Box>
          </CardContent>
        </Card>
      </Fade>
    );
  };
  const renderDashboardTCC = () => (
    <Fade in timeout={600}>
      <Card elevation={2} sx={{ borderRadius: 3 }}>
        <CardContent sx={{ p: 0 }}>
          <Suspense
            fallback={
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            }
          >
            <DashboardTCC />
          </Suspense>
        </CardContent>
      </Card>
    </Fade>
  );

  const handleBack = () => navigate(-1);

  // ---------- RENDER ROOT ----------
  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: darkMode ? 'grey.900' : alpha(theme.palette.primary.main, 0.02),
        transition: 'background-color .3s',
      }}
    >
      {/* Header */}
      <DashboardHeader darkMode={darkMode} setDarkMode={setDarkMode} onBack={handleBack} userName={user?.nome} />

      {/* Sidebar */}
      <DashboardSidebar
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />

      {/* Main */}
      <Box sx={{ maxWidth: 1400, mx: 'auto', p: { xs: 2, md: 3 } }}>
        {/* Botão Menu Mobile */}
        <Box sx={{ display: { xs: 'block', md: 'none' }, mb: 2 }}>
          <Button
            variant="outlined"
            startIcon={<MenuIcon />}
            onClick={() => setSidebarOpen(true)}
            sx={{ borderRadius: 2, minHeight: 44 }}
          >
            Menu
          </Button>
        </Box>

        {/* Tabs */}
        <Paper elevation={1} sx={{ mb: 3, borderRadius: 3, overflow: 'hidden' }}>
          <Tabs
            value={activeTab}
            onChange={(_, v) => setActiveTab(v)}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
              '& .MuiTab-root': {
                minHeight: 64,
                minWidth: { xs: 120, sm: 160 },
                fontWeight: 700,
                textTransform: 'none',
                fontSize: '0.95rem',
                px: 2,
                '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.05) },
                '&.Mui-selected': { bgcolor: alpha(theme.palette.primary.main, 0.08) },
              },
            }}
          >
            <Tab icon={<DashboardIcon />} label="Dashboard" iconPosition="start" />
            <Tab icon={<ArticleIcon />} label="Propostas" iconPosition="start" />
            <Tab icon={<AssignmentIcon />} label="Projetos" iconPosition="start" />
            <Tab icon={<DescriptionIcon />} label="Monografias" iconPosition="start" />
            <Tab icon={<EventNoteIcon />} label="Defesas" iconPosition="start" />
            <Tab icon={<VisibilityIcon />} label="Avaliações" iconPosition="start" />
            <Tab icon={<ArchiveIcon />} label="Arquivamento" iconPosition="start" />
            <Tab icon={<AnalyticsIcon />} label="Dashboard TCC" iconPosition="start" />
          </Tabs>
        </Paper>

        {/* Error */}
        {error && (
          <Slide in timeout={300}>
            <Alert
              severity="error"
              sx={{
                mb: 2,
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.error.main, 0.28)}`,
                bgcolor: alpha(theme.palette.error.main, 0.06),
              }}
              action={
                <Button color="inherit" size="small" onClick={fetchDados} sx={{ borderRadius: 2 }}>
                  Tentar Novamente
                </Button>
              }
            >
              {error}
            </Alert>
          </Slide>
        )}

        {/* Conteúdo */}
        {loading ? (
          <LoadingSkeleton />
        ) : (
          <Box>
            {activeTab === 0 && renderDashboardStatistics()}
            {activeTab === 1 && renderPropostas()}
            {activeTab === 2 && renderProjetos()}
            {activeTab === 3 && renderMonografias()}
            {activeTab === 4 && renderDefesas()}
            {activeTab === 5 && renderAvaliacoes()}
            {activeTab === 6 && <RenderArquivamento />}
            {activeTab === 7 && renderDashboardTCC()}
          </Box>
        )}
      </Box>

      {/* Toast */}
      <ToastNotification
        open={toast.open}
        message={toast.message}
        severity={toast.severity}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
      />

      {/* Diálogos (fullscreen no mobile) */}
      {/* Rejeitar Proposta */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        PaperProps={{ sx: { borderRadius: 3 } }}
        fullScreen={fullScreenDialog}
      >
        <DialogTitle sx={{ bgcolor: alpha(theme.palette.error.main, 0.1), color: theme.palette.error.main }}>
                  Rejeitar Proposta
                </DialogTitle>
                <DialogContent sx={{ p: 3 }}>
                  <TextField
                    autoFocus
                    margin="dense"
                    label="Motivo da Rejeição"
                    fullWidth
                    multiline
                    rows={4}
                    value={motivoRejeicao}
                    onChange={(e) => setMotivoRejeicao(e.target.value)}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2
                      }
                    }}
                  />
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                  <Button onClick={() => setDialogOpen(false)} sx={{ borderRadius: 2 }}>
                    Cancelar
                  </Button>
                  <Button 
                    onClick={handleRejeitarProposta} 
                    color="error" 
                    variant="contained"
                    sx={{ borderRadius: 2 }}
                  >
                    Confirmar Rejeição
                  </Button>
                </DialogActions>
              </Dialog>
        
              {/* Diálogo de Aprovação de Proposta */}
              <Dialog 
                open={approveDialogOpen} 
                onClose={() => setApproveDialogOpen(false)}
                PaperProps={{
                  sx: { borderRadius: 3 }
                }}
              >
                <DialogTitle sx={{ bgcolor: alpha(theme.palette.success.main, 0.1), color: theme.palette.success.main }}>
                  Aprovar Proposta
                </DialogTitle>
                <DialogContent sx={{ p: 3 }}>
                  <TextField
                    autoFocus
                    margin="dense"
                    label="Observações"
                    fullWidth
                    multiline
                    rows={4}
                    value={observacoesProposta}
                    onChange={(e) => setObservacoesProposta(e.target.value)}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2
                      }
                    }}
                  />
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                  <Button onClick={() => setApproveDialogOpen(false)} sx={{ borderRadius: 2 }}>
                    Cancelar
                  </Button>
                  <Button 
                    onClick={handleAprovarPropostaConfirm} 
                    color="success" 
                    variant="contained"
                    sx={{ borderRadius: 2 }}
                  >
                    Confirmar Aprovação
                  </Button>
                </DialogActions>
              </Dialog>
        
              {/* Diálogo de Designação de Supervisor */}
              <Dialog 
                open={supervisorDialogOpen} 
                onClose={() => setSupervisorDialogOpen(false)}
                PaperProps={{
                  sx: { borderRadius: 3 }
                }}
              >
                <DialogTitle sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), color: theme.palette.primary.main }}>
                  {selectedProposta?.supervisorId ? 'Alterar Supervisor' : 'Designar Supervisor'}
                </DialogTitle>
                <DialogContent sx={{ p: 3 }}>
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    Selecione um supervisor para a proposta:{' '}
                    <strong>{selectedProposta?.titulo}</strong>
                  </Typography>
                  <FormControl fullWidth>
                    <InputLabel id="supervisor-select-label">Supervisor</InputLabel>
                    <Select
                      labelId="supervisor-select-label"
                      value={selectedSupervisor}
                      onChange={(e) => setSelectedSupervisor(e.target.value)}
                      label="Supervisor"
                      sx={{
                        borderRadius: 2
                      }}
                    >
                      {supervisores.map((supervisor) => (
                        <MenuItem key={supervisor.id} value={supervisor.id}>
                          {supervisor.Usuario?.nome || `Supervisor #${supervisor.id}`} -{' '}
                          {supervisor.area_especializacao}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                  <Button onClick={() => setSupervisorDialogOpen(false)} sx={{ borderRadius: 2 }}>
                    Cancelar
                  </Button>
                  <Button
                    onClick={handleDesignarSupervisor}
                    color="primary"
                    variant="contained"
                    disabled={!selectedSupervisor}
                    sx={{ borderRadius: 2 }}
                  >
                    Confirmar
                  </Button>
                </DialogActions>
              </Dialog>
        
              {/* Diálogo de Encaminhamento de Projeto para Revisor */}
              <Dialog
                open={projetoRevisorDialogOpen}
                onClose={() => setProjetoRevisorDialogOpen(false)}
                PaperProps={{
                  sx: { borderRadius: 3 }
                }}
              >
                <DialogTitle sx={{ bgcolor: alpha(theme.palette.info.main, 0.1), color: theme.palette.info.main }}>
                  Encaminhar Projeto para Revisor
                </DialogTitle>
                <DialogContent sx={{ p: 3 }}>
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    Selecione um revisor para o projeto:{' '}
                    <strong>{selectedProjeto?.proposta?.titulo}</strong>
                  </Typography>
                  <FormControl fullWidth>
                    <InputLabel id="revisor-select-label">Revisor</InputLabel>
                    <Select
                      labelId="revisor-select-label"
                      value={selectedRevisor}
                      onChange={(e) => setSelectedRevisor(e.target.value)}
                      label="Revisor"
                      sx={{
                        borderRadius: 2
                      }}
                    >
                      {revisores.map((revisor) => (
                        <MenuItem key={revisor.id} value={revisor.id}>
                          {revisor.nome || `Revisor #${revisor.id}`}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                  <Button onClick={() => setProjetoRevisorDialogOpen(false)} sx={{ borderRadius: 2 }}>
                    Cancelar
                  </Button>
                  <Button
                    onClick={handleEncaminharProjetoParaRevisor}
                    color="primary"
                    variant="contained"
                    disabled={!selectedRevisor}
                    sx={{ borderRadius: 2 }}
                  >
                    Encaminhar
                  </Button>
                </DialogActions>
              </Dialog>
        
              {/* Diálogo de Encaminhamento de Monografia para Revisão */}
              <Dialog
                open={monografiaRevisorDialogOpen}
                onClose={() => setMonografiaRevisorDialogOpen(false)}
                PaperProps={{
                  sx: { borderRadius: 3 }
                }}
              >
                <DialogTitle sx={{ bgcolor: alpha(theme.palette.warning.main, 0.1), color: theme.palette.warning.main }}>
                  Encaminhar Monografia para Revisão
                </DialogTitle>
                <DialogContent sx={{ p: 3 }}>
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    Selecione um revisor para a monografia:{' '}
                    <strong>
                      {selectedMonografia?.titulo ||
                        selectedMonografia?.projeto?.proposta?.titulo ||
                        `Monografia #${selectedMonografia?.id}`}
                    </strong>
                  </Typography>
                  <FormControl fullWidth>
                    <InputLabel id="revisor-monografia-select-label">Revisor</InputLabel>
                    <Select
                      labelId="revisor-monografia-select-label"
                      value={selectedRevisorMonografia}
                      onChange={(e) => setSelectedRevisorMonografia(e.target.value)}
                      label="Revisor"
                      sx={{
                        borderRadius: 2
                      }}
                    >
                      {revisores.map((revisor) => (
                        <MenuItem key={revisor.id} value={revisor.id}>
                          {revisor.nome || `Revisor #${revisor.id}`}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                  <Button onClick={() => setMonografiaRevisorDialogOpen(false)} sx={{ borderRadius: 2 }}>
                    Cancelar
                  </Button>
                  <Button
                    onClick={handleEncaminharMonografiaParaRevisao}
                    color="primary"
                    variant="contained"
                    disabled={!selectedRevisorMonografia}
                    sx={{ borderRadius: 2 }}
                  >
                    Encaminhar
                  </Button>
                </DialogActions>
              </Dialog>
            </Box>
          );
        }
      
  export default ChefeDepartamentoDashboardPage;